// This is based of https://github.com/emotion-js/facepaint
import { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { BorderScale, BorderStyle, Color, SpaceScale } from './tokens';
import {
  DesignTokens,
  Theme,
  Responsive,
  ResponsiveStyle,
  ResponsiveSpacing,
  ResponsiveColor,
  ColorFn,
  ResponsiveFactory,
  RadiusFn,
  ThemeKeyFactory,
  BorderFns,
  FontSize,
  ResponsiveFont,
  SpacingFn,
  ZIndexFn,
} from './types';

export const createResponsive: (token: DesignTokens) => Responsive = (
  token
) => (values, createCSSObject): CSSInterpolation => {
  if (values === null || values === undefined) {
    return;
  }

  if (!Array.isArray(values)) {
    return createCSSObject(values, token);
  }

  const defaultValue = values[0];
  const defaultCSSRule =
    defaultValue === null || defaultValue === undefined
      ? null
      : createCSSObject(defaultValue, token);
  const cssStyling = defaultCSSRule ? (defaultCSSRule as CSSObject) : {};

  return token.breakpoints.reduce((memo, breakpoint, index) => {
    const valueForNextBreakpoint = values[index + 1];

    if (
      valueForNextBreakpoint === null ||
      valueForNextBreakpoint === undefined
    ) {
      return memo;
    }

    const mediaQuery = `@media (min-width: ${breakpoint})`;
    const rules = createCSSObject(valueForNextBreakpoint, token);

    return { ...memo, [mediaQuery]: rules };
  }, cssStyling);
};

export const createResponsiveStyle: ResponsiveFactory<ResponsiveStyle> = (
  responsive
) => (values, propertyNames): CSSInterpolation =>
  responsive(values, (value) =>
    propertyNames.reduce((memo, cssName) => ({ ...memo, [cssName]: value }), {})
  );

export const createResponsiveSpacing: ResponsiveFactory<ResponsiveSpacing> = (
  responsive
) => (values, propertyNames): CSSInterpolation =>
  responsive(values, (scale: SpaceScale, token: DesignTokens) =>
    propertyNames.reduce(
      (memo, cssName) => ({
        ...memo,
        [cssName]: token.spacingScale * scale + 'px',
      }),
      {}
    )
  );

export const createResponsiveColor: ResponsiveFactory<ResponsiveColor> = (
  responsive
) => (values, propertyNames): CSSInterpolation =>
  responsive(values, (color: Color, token: DesignTokens) =>
    propertyNames.reduce(
      (memo, cssName) => ({
        ...memo,
        [cssName]: token.colors[color],
      }),
      {}
    )
  );

export const createResponsiveFont: ResponsiveFactory<ResponsiveFont> = (
  responsive
) => (values): CSSInterpolation =>
  responsive(
    values,
    (fontSize: FontSize, token: DesignTokens) => token.fontSizes[fontSize]
  );

export const createColor: ThemeKeyFactory<ColorFn> = (token) => (
  color
): string => token.colors[color as Color];

export const createRadius: ThemeKeyFactory<RadiusFn> = (token) => (
  radius
): { borderRadius: string } =>
  radius
    ? {
        borderRadius:
          radius === 'round' ? '100%' : token.radiusScale * radius + 'px',
      }
    : { borderRadius: 'unset' };

export const createSpacing: ThemeKeyFactory<SpacingFn> = (token) => (
  scale
): number => token.spacingScale * (scale || 0);

export const createBorder: ThemeKeyFactory<BorderFns> = (token) => ({
  width: (
    width?: BorderScale,
    key = 'borderWidth'
  ): { [key: string]: string } => {
    if (width == null) return {};

    return {
      [key]: width * token.borderScale + 'px',
    };
  },
  color: (color?: Color): { borderColor?: string | undefined } => {
    if (color == null) return {};

    return {
      borderColor: createColor(token)(color),
    };
  },
  style: (style?: BorderStyle): { borderStyle?: string } => {
    if (style == null) return {};

    return {
      borderStyle: token.borderStyle[style],
    };
  },
});

export const createZIndex: ThemeKeyFactory<ZIndexFn> = token => zIndex =>
  token.zIndices[zIndex] || 'auto';

export const createTheme = (tokens: DesignTokens): Theme => {
  const responsive = createResponsive(tokens);

  return {
    tokens,
    responsive,
    responsiveStyle: createResponsiveStyle(responsive),
    responsiveSpacing: createResponsiveSpacing(responsive),
    responsiveColor: createResponsiveColor(responsive),
    responsiveFont: createResponsiveFont(responsive),
    color: createColor(tokens),
    radius: createRadius(tokens),
    border: createBorder(tokens),
    spacing: createSpacing(tokens),
    zIndex: createZIndex(tokens)
  };
};
